<template>
  <!-- 分栏组件容器 -->
  <div class="id_11">
    <!-- 分栏容器，使用flex布局实现多栏并排 -->
    <div class="columns-container" ref="columnsContainer">
      <!-- 分栏包装器，负责承载单个分栏 -->
      <div
        v-for="(column, index) in columns"
        :key="column.id"
        class="column-wrapper"
        :data-column-id="column.id"
      >
        <!-- 分栏主体，包含内容区 -->
        <div
          class="column"
          :style="{
            background: column.bgColor,
            borderColor: column.borderColor,
            minHeight: column.minHeight + 'px'
          }"
        >
          <!-- 分栏内容区（使用命令式DOM管理，避免v-html覆盖子组件） -->
          <div class="column-content"></div>
        </div>
      </div>
    </div>

    <ContentInsertion
      ref="contentInsertion"
      :features="insertFeatures"
      @insert-image="handleImageInsert"
      @insert-highlight="handleHighlightInsert"
      @insert-columns="handleColumnsInsert"
      @insert-table="handleTableInsert"
      @insert-file="handleFileInsert"
    />
  </div>
</template>

<script>
/**
 * 分栏组件 - id_11
 *
 * 功能：
 * 1. 根据 props.data.columns 渲染多栏布局
 * 2. 恢复分栏内纯文本与嵌套子组件（高亮/表格/文件）
 * 3. 支持向选中分栏插入图片与子组件
 * 4. 通过 saveState 将分栏状态同步回 props.data
 *
 * 数据流转：
 * 1. mounted 时 initColumns 初始化分栏，再 initColumnComponents 恢复子组件
 * 2. 插入操作更新 columns / DOM 后调用 saveState
 * 3. 监听 data.columns 变化时重新规范化并应用布局
 */
import ContentInsertion from './ContentInsertion.vue'
import Id10 from './id_10.vue'
import Id12 from './id_12.vue'
import Id13 from './id_13.vue'
import Vue from 'vue'

export default {
  name: 'id_11',
  props: {
    /**
     * 外部传入的组件状态数据
     * @type {Object}
     * @property {Array} columns - 分栏数据数组，包含 id、content、width、bgColor、borderColor、minHeight、components
     */
    data: Object
  },
  components: {
    ContentInsertion
  },
  data () {
    return {
      /**
       * 分栏数据数组
       * @type {Array<Column>}
       */
      columns: [],
      /**
       * 当前选中的分栏索引，-1 表示未选中
       * @type {number}
       */
      selectedIndex: -1,
      /**
       * 边框颜色预设列表
       * @type {Array<string>}
       */
      borderColorOptions: ['#EEF3FE', '#F7F3FF', '#FBF2F9', '#FFEDEA', '#FFF6EA', '#FFFBE4', '#F5F8E8', '#F0F9F1', '#EEF7F6', '#EBF8FE'],
      /**
       * 背景颜色预设列表
       * @type {Array<string>}
       */
      bgColorOptions: ['#EEF3FE', '#F7F3FF', '#FBF2F9', '#FFEDEA', '#FFF6EA', '#FFFBE4', '#F5F8E8', '#F0F9F1', '#EEF7F6', '#EBF8FE'],
      /**
       * 分栏ID计数器
       * @type {number}
       */
      columnIdCounter: Date.now(),
      /**
       * 动态创建的子组件实例列表
       * @type {Array}
       */
      mountedComponents: [],
      /**
       * 组件ID计数器
       * @type {number}
       */
      componentIdCounter: 0,
      /**
       * 组件类型映射表
       * @type {Object}
       */
      componentMap: {
        highlight: Id13,
        table: Id12,
        file: Id10
      },
      /**
       * 可用插入功能列表
       * @type {Array<string>}
       */
      insertFeatures: ['image', 'highlight', 'table', 'file']
    }
  },
  watch: {
    /**
     * 监听外部数据变化，重新规范化并应用布局
     */
    'data.columns': {
      handler () {
        if (this.data && this.data.columns && this.data.columns.length > 0) {
          const rawColumns = JSON.parse(JSON.stringify(this.data.columns))
          this.columns = rawColumns.map((col, index) => this.normalizeColumnData(col, index))
          this.$nextTick(() => {
            this.recalculateLayout()
          })
        }
      },
      deep: true
    }
  },
  methods: {
    /**
     * 初始化分栏数据
     * @description 优先从 props.data.columns 恢复，否则创建默认两栏布局
     */
    initColumns () {
      if (this.data && this.data.columns) {
        const rawColumns = JSON.parse(JSON.stringify(this.data.columns))
        this.columns = rawColumns.map((col, index) => this.normalizeColumnData(col, index))
      } else {
        this.columns = [
          this.createColumn(0, 49.75),
          this.createColumn(1, 49.75)
        ]
      }

      this.$nextTick(() => {
        const contentEls = this.getDirectColumnContentEls()
        this.columns.forEach((column, index) => {
          const contentEl = contentEls[index]
          if (contentEl && column.content && column.content.trim() !== '') {
            contentEl.innerHTML = column.content
          }
        })
        this.recalculateLayout()
      })
    },

    /**
     * 规范化分栏数据，确保属性类型正确
     * @param {Object} col - 原始分栏数据
     * @param {number} index - 分栏索引
     * @returns {Object} 规范化后的分栏数据
     */
    normalizeColumnData (col, index) {
      let width = 50
      if (col.width !== undefined && col.width !== null) {
        width = this.parseWidthValue(col.width)
      } else if (col.flexBasis !== undefined && col.flexBasis !== null) {
        width = this.parseWidthValue(col.flexBasis)
      }

      let minHeight = 40
      if (col.minHeight !== undefined && col.minHeight !== null) {
        if (typeof col.minHeight === 'string') {
          minHeight = parseInt(col.minHeight, 10) || 40
        } else {
          minHeight = col.minHeight
        }
      }

      let id = col.id
      if (id === undefined || id === null) {
        this.columnIdCounter++
        id = this.columnIdCounter
      } else if (id > this.columnIdCounter) {
        this.columnIdCounter = id
      }

      return {
        id: id,
        content: col.content !== undefined && col.content !== null ? col.content : (col.components && col.components.length > 0 ? null : ''),
        components: col.components || [],
        width: width,
        bgColor: col.bgColor || this.bgColorOptions[index % this.bgColorOptions.length],
        borderColor: col.borderColor || this.borderColorOptions[index % this.borderColorOptions.length],
        minHeight: minHeight
      }
    },

    /**
     * 解析宽度值，支持 "49.75%"、"49.75" 或数字
     * @param {string|number} value
     * @returns {number}
     */
    parseWidthValue (value) {
      if (typeof value === 'number') {
        return value
      }
      if (typeof value === 'string') {
        const cleaned = value.replace(/[%\s]/g, '')
        const num = parseFloat(cleaned)
        return isNaN(num) ? 50 : num
      }
      return 50
    },

    /**
     * 创建分栏数据对象
     * @param {number} index - 分栏索引
     * @param {number} width - 分栏宽度百分比
     * @returns {Object}
     */
    createColumn (index, width) {
      const borderIndex = index % this.borderColorOptions.length
      const bgIndex = index % this.bgColorOptions.length
      this.columnIdCounter++
      return {
        id: this.columnIdCounter,
        content: '',
        components: [],
        width: width,
        bgColor: this.bgColorOptions[bgIndex],
        borderColor: this.borderColorOptions[borderIndex],
        minHeight: 40
      }
    },

    /**
     * 选择分栏
     * @param {number} index
     */
    selectColumn (index) {
      this.selectedIndex = index
    },

    /**
     * 获取所有直接分栏内容区域元素（排除嵌套组件中的 .column-content）
     * @returns {HTMLElement[]}
     */
    getDirectColumnContentEls () {
      const container = this.$refs.columnsContainer
      if (!container) return []
      return Array.from(container.children).map(w => w.querySelector(':scope > .column > .column-content')).filter(Boolean)
    },

    /**
     * 获取当前选中分栏的内容区域 DOM 元素
     * @returns {HTMLElement|null}
     */
    getSelectedColumnContentEl () {
      if (this.selectedIndex < 0 || this.selectedIndex >= this.columns.length) {
        return null
      }
      return this.getDirectColumnContentEls()[this.selectedIndex] || null
    },

    /**
     * 获取当前选中分栏的数据对象
     * @returns {Object|null}
     */
    getSelectedColumn () {
      if (this.selectedIndex < 0 || this.selectedIndex >= this.columns.length) {
        return null
      }
      return this.columns[this.selectedIndex]
    },

    /**
     * 打开插入功能面板
     */
    openInsertPanel () {
      this.$refs.contentInsertion.openPanel()
    },

    /**
     * 处理图片插入事件
     */
    handleImageInsert () {
      const contentEl = this.getSelectedColumnContentEl()
      const column = this.getSelectedColumn()

      if (!contentEl) {
        console.warn('未找到选中分栏的内容区域')
        return
      }

      if (!this.$api || !this.$api.upImg) {
        console.error('图片上传API不可用')
        return
      }

      this.$api.upImg().then((url) => {
        if (!url) {
          console.error('图片上传失败：返回URL为空')
          return
        }

        const imgHTML = `<img src="${url}" style="max-width:100%;display:block;margin:8px 0;"><div style="font-size:12px;color:#909399;text-align:center;margin-bottom:8px;">(图片介绍，不需要可自行删除)</div>`

        if (column && column.components && column.components.length > 0) {
          column.components.push({ type: 'text', content: imgHTML })
          contentEl.insertAdjacentHTML('beforeend', imgHTML)
        } else if (column) {
          if (!column.content || column.content === '') {
            column.content = imgHTML
          } else {
            column.content += imgHTML
          }
          contentEl.innerHTML = column.content
        } else {
          contentEl.insertAdjacentHTML('beforeend', imgHTML)
        }

        this.saveState()
      }).catch((err) => {
        console.error('图片上传失败:', err)
      })
    },

    /**
     * 处理高亮块插入事件
     */
    handleHighlightInsert (defaultData) {
      this.insertVueComponentToColumn('highlight', defaultData || { bgColor: '#FFF3CD', borderColor: '#FFC107' })
    },

    /**
     * 处理表格组件插入事件
     */
    handleTableInsert (defaultData) {
      this.insertVueComponentToColumn('table', defaultData || {
        table: [
          [{ content: '标题1' }, { content: '标题2' }, { content: '标题3' }],
          [{ content: '内容1' }, { content: '内容2' }, { content: '内容3' }]
        ],
        hasHeaderRow: true
      })
    },

    /**
     * 处理文件组件插入事件
     */
    handleFileInsert (defaultData) {
      this.insertVueComponentToColumn('file', defaultData || { list: '[]' })
    },

    /**
     * 处理分栏组件插入事件（分栏内不支持再插入分栏）
     */
    handleColumnsInsert () {
      console.warn('分栏内不支持再插入分栏组件')
    },

    /**
     * 向选中分栏中插入 Vue 子组件
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     */
    insertVueComponentToColumn (type, data) {
      const contentEl = this.getSelectedColumnContentEl()
      const column = this.getSelectedColumn()

      if (!contentEl) {
        console.warn('未找到选中分栏的内容区域')
        return
      }

      // 若当前为纯文本内容，先转为 DOM 并记入 components
      if (column && column.content && column.content.trim() !== '' && (!column.components || column.components.length === 0)) {
        const p = document.createElement('p')
        p.innerHTML = column.content
        contentEl.insertBefore(p, contentEl.firstChild)
        column.components = [{ type: 'text', content: column.content }]
        column.content = null
      }

      // 先追加占位符，再由 insertVueComponentAt 替换为组件根节点
      const placeholder = document.createElement('div')
      contentEl.appendChild(placeholder)
      this.insertVueComponentAt(type, data, placeholder)

      if (column) {
        if (!column.components) {
          column.components = []
        }
        column.content = null
      }

      this.saveState()
    },

    /**
     * 根据 columns 数据更新各分栏 flexBasis
     */
    recalculateLayout () {
      const container = this.$refs.columnsContainer
      if (!container) return

      const wrappers = Array.from(container.children)
      wrappers.forEach((wrapper, index) => {
        if (this.columns[index]) {
          wrapper.style.flexBasis = this.columns[index].width + '%'
        }
      })
    },

    /**
     * 处理文档点击：点击组件外部时取消选中
     * @param {MouseEvent} event
     */
    handleDocumentClick (event) {
      if (this.selectedIndex !== -1 && !this.$el.contains(event.target)) {
        this.selectedIndex = -1
      }
    },

    /**
     * 保存组件状态到 props.data.columns
     */
    saveState () {
      if (!this.data) return

      const spacingPercent = 1
      const totalSpacingPercent = (this.columns.length - 1) * spacingPercent
      const availableWidthPercent = 100 - totalSpacingPercent

      const domToInstanceMap = {}
      this.mountedComponents.forEach(item => {
        if (item.instance && item.instance.$el) {
          domToInstanceMap[item.instance.$el] = item
        }
      })

      const contentEls = this.getDirectColumnContentEls()

      this.data.columns = this.columns.map((col, colIndex) => {
        const flexBasisPercent = (col.width / 100) * availableWidthPercent
        const savedCol = {
          ...JSON.parse(JSON.stringify(col)),
          flexBasis: flexBasisPercent.toFixed(2) + '%',
          width: col.width
        }

        if (this.mountedComponents.length > 0 && contentEls[colIndex]) {
          const contentEl = contentEls[colIndex]
          const hasMountedComponents = this.mountedComponents.some(item => {
            return item.instance && item.instance.$el && contentEl.contains(item.instance.$el)
          })

          if (hasMountedComponents) {
            const collectedComponents = this.collectColumnComponents(contentEl, domToInstanceMap)
            if (collectedComponents.length > 0) {
              savedCol.content = null
              savedCol.components = collectedComponents
            }
          }
        }

        return savedCol
      })
    },

    /**
     * 从列内容区域收集子组件和文本数据
     * @param {HTMLElement} contentEl
     * @param {Object} domToInstanceMap
     * @returns {Array}
     */
    collectColumnComponents (contentEl, domToInstanceMap) {
      const result = []
      if (!contentEl) return result

      const children = contentEl.children
      for (let i = 0; i < children.length; i++) {
        const child = children[i]

        if (child.classList && child.classList.contains('empty-text-paragraph') && !child.textContent) {
          continue
        }

        const classList = child.classList
        let componentId = null
        if (classList) {
          for (let j = 0; j < classList.length; j++) {
            const className = classList[j]
            if (className.startsWith('id_')) {
              componentId = parseInt(className.replace('id_', ''), 10)
              break
            }
          }
        }

        if (componentId && componentId >= 10 && componentId <= 13) {
          const componentItem = domToInstanceMap[child]
          if (componentItem && componentItem.instance) {
            const componentInstance = componentItem.instance
            if (typeof componentInstance.saveState === 'function') {
              componentInstance.saveState()
            }
            const idMap = { 'id_10': 10, 'id_11': 11, 'id_12': 12, 'id_13': 13 }
            const componentName = componentInstance.$options.name
            const numericId = idMap[componentName] || componentId
            if (numericId) {
              const componentData = this.buildComponentData(numericId, componentInstance.data, child)
              if (componentData) {
                result.push({ type: 'component', ...componentData })
              }
            }
          }
        } else {
          const textContent = child.innerHTML
          if (textContent && textContent.trim() !== '' && textContent !== '<br>') {
            result.push({ type: 'text', content: textContent })
          }
        }
      }
      return result
    },

    /**
     * 构建子组件的返回数据
     * @param {Number} id
     * @param {Object} propsData
     * @returns {Object|null}
     */
    buildComponentData (id, propsData) {
      switch (id) {
        case 13:
          return {
            id: 13,
            text: propsData.text,
            bgColor: propsData.bgColor || '',
            borderColor: propsData.borderColor || '',
            components: propsData.components || []
          }
        case 12:
          return {
            id: 12,
            table: propsData.table || [],
            rowBgColors: propsData.rowBgColors || {},
            colBgColors: propsData.colBgColors || {},
            cellBgColors: propsData.cellBgColors || {},
            isFullWidth: propsData.isFullWidth || false,
            hasHeaderRow: propsData.hasHeaderRow || false,
            hasHeaderCol: propsData.hasHeaderCol || false,
            colWidths: propsData.colWidths || [],
            rowHeights: propsData.rowHeights || [],
            mergedCells: propsData.mergedCells || []
          }
        case 10: {
          let listData = propsData.list
          if (typeof listData === 'string') {
            try { listData = JSON.parse(listData) || [] } catch (e) { listData = [] }
          }
          return { id: 10, list: listData || [] }
        }
        default:
          return null
      }
    },

    /**
     * 从数据中恢复所有分栏的子组件和文本内容
     * @description 两阶段：先构建 DOM（文本直接渲染、组件用占位符），再替换为 Vue 组件
     */
    initColumnComponents () {
      if (!this.columns || this.columns.length === 0) return

      const typeMap = {
        13: 'highlight',
        12: 'table',
        10: 'file'
      }

      const allPlaceholders = []

      this.columns.forEach((column, colIndex) => {
        if (!column.components || !Array.isArray(column.components) || column.components.length === 0) {
          return
        }

        const contentEl = this.getDirectColumnContentEls()[colIndex]
        if (!contentEl) return

        contentEl.innerHTML = ''

        const componentPlaceholders = []
        column.components.forEach((item, index) => {
          if (item.type === 'text') {
            const p = document.createElement('p')
            p.innerHTML = item.content || ''
            contentEl.appendChild(p)
          } else if (item.type === 'html') {
            contentEl.insertAdjacentHTML('beforeend', item.content || '')
          } else if (item.type === 'component') {
            const placeholder = document.createElement('div')
            placeholder.setAttribute('data-column-id', column.id)
            placeholder.setAttribute('data-component-index', index)
            contentEl.appendChild(placeholder)
            componentPlaceholders.push({
              placeholder,
              compData: item
            })
          }
        })

        allPlaceholders.push(...componentPlaceholders)
      })

      if (allPlaceholders.length > 0) {
        this.$nextTick(() => {
          allPlaceholders.forEach(({ placeholder, compData }) => {
            const type = typeMap[compData.id]
            if (type) {
              this.insertVueComponentAt(type, compData, placeholder)
            }
          })
        })
      }
    },

    /**
     * 动态创建 Vue 组件实例并插入/替换目标元素
     * @param {String} type - 组件类型
     * @param {Object} data - 组件 props 数据
     * @param {HTMLElement} placeholder - 占位符或目标容器
     */
    insertVueComponentAt (type, data, placeholder) {
      const Component = this.componentMap[type]
      if (!Component) {
        console.error(`未支持的组件类型: ${type}`)
        return
      }

      if (!placeholder) {
        console.error('无效的目标元素')
        return
      }

      this.componentIdCounter++

      const ComponentConstructor = Vue.extend(Component)
      const propsData = { data: data }
      if (type === 'highlight') {
        propsData.embedded = true
      }

      const componentInstance = new ComponentConstructor({
        propsData: propsData
      })

      const tempContainer = document.createElement('div')
      tempContainer.setAttribute('contenteditable', 'false')
      componentInstance.$mount(tempContainer)

      const componentEl = componentInstance.$el

      if (componentEl) {
        componentEl.setAttribute('contenteditable', 'false')

        if (placeholder.parentNode) {
          placeholder.parentNode.replaceChild(componentEl, placeholder)
        } else {
          placeholder.appendChild(componentEl)
        }

        this.mountedComponents.push({
          id: this.componentIdCounter,
          instance: componentInstance
        })
      } else {
        console.error('无法插入组件：组件实例创建失败')
        componentInstance.$destroy()
      }
    },

    /**
     * 销毁所有动态创建的子组件
     */
    destroyComponents () {
      this.mountedComponents.forEach(item => {
        if (item.instance) {
          const componentEl = item.instance.$el
          item.instance.$destroy()
          if (componentEl && componentEl.parentNode) {
            componentEl.parentNode.removeChild(componentEl)
          }
        }
      })
      this.mountedComponents = []
      this.componentIdCounter = 0
    }
  },
  mounted () {
    this.initColumns()
    this.$nextTick(() => {
      this.initColumnComponents()
    })
    document.addEventListener('mousedown', this.handleDocumentClick)
  },
  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleDocumentClick)
    this.destroyComponents()
  }
}
</script>

<style lang="scss" scoped>
/**
 * 分栏组件样式
 * 使用flex布局实现多栏并排，通过gap控制分栏间距
 */
.id_11 {
  padding: 12px 0;
  padding-top: 40px;
  background-color: transparent;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  /** 分栏容器 */
  .columns-container {
    display: flex;
    padding: 0 2px;
    transition: all 0.3s ease;
    position: relative;
    width: 100%;
    box-sizing: border-box;
    gap: 1%;
  }

  /** 分栏包装器 */
  .column-wrapper {
    flex-shrink: 0;
    flex-grow: 0;
    flex-basis: 0%;
    position: relative;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    border: none;
    overflow: hidden;
  }

  /** 分栏主体 */
  .column {
    padding: 12px;
    min-height: 40px;
    border: 2px solid;
    border-radius: 8px;
    position: relative;
    cursor: text;
    overflow: hidden;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    transition: box-shadow 0.2s ease;
    white-space: pre-wrap;
    word-break: break-word;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    /** 分栏内容区 */
    .column-content {
      font-size: 14px;
      line-height: 22px;
      color: #303133;
      outline: none;
      word-break: break-word;
      transition: min-height 0.2s ease;

      /deep/ .id_10, /deep/ .id_12, /deep/ .id_13 {
        padding: 0;
        padding-top: 12px;
      }

      &:empty:before {
        content: '点击输入内容...';
        color: #909399;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/id_11.vue